import type { RescueDocument, RescueRequiredDocument } from '../../lib/documentQueries';
import { certificateFormName, daysUntil, expiringSoon, stateName } from '../../lib/documentLabels';
import { FileButton, FileCard, FileLink } from '../../components/Documents/FileCard';
import StatusPill, { type PillTone } from '../../components/Documents/StatusPill';
import UploadBox from '../../components/Documents/UploadBox';
import { formatDate, formatFileSize } from '../../lib/utils';
import styles from './Account.module.scss';

function tone(doc: RescueDocument): PillTone {
  if (doc.status === 'REJECTED' || doc.expired) {
    return 'rejected';
  }
  if (doc.status === 'PENDING' || expiringSoon(doc)) {
    return 'pending';
  }
  return 'approved';
}

/** The letter's full status line, which sits under its file as a badge. */
function letterStatus(doc: RescueDocument): string {
  if (doc.status === 'PENDING') {
    return 'Pending review by the Fi team';
  }
  if (doc.status === 'REJECTED') {
    return `Not accepted · Reviewed ${formatDate(doc.reviewedAt ?? doc.uploadedAt)}`;
  }
  const approved = `Approved ${formatDate(doc.reviewedAt ?? doc.uploadedAt)}`;
  if (doc.neverExpires || !doc.expirationDate) {
    return `${approved} · Doesn't expire`;
  }
  return `${approved} · ${doc.expired ? 'Expired' : 'Expires'} ${formatDate(doc.expirationDate)}`;
}

/** A certificate carries its status as a short pill beside the file, with the dates under its name. */
function certificatePill(doc: RescueDocument): string {
  if (doc.status === 'PENDING') {
    return 'In review';
  }
  if (doc.status === 'REJECTED') {
    return 'Not accepted';
  }
  if (doc.expired) {
    return 'Expired';
  }
  if (expiringSoon(doc) && doc.expirationDate) {
    const days = daysUntil(doc.expirationDate);
    return `Expires in ${days} day${days === 1 ? '' : 's'}`;
  }
  return 'Approved';
}

function certificateSubtitle(doc: RescueDocument): string {
  if (doc.status !== 'APPROVED') {
    return `${formatFileSize(doc.sizeBytes)} · uploaded ${formatDate(doc.uploadedAt)}`;
  }
  const approved = `Approved ${formatDate(doc.reviewedAt ?? doc.uploadedAt)}`;
  if (doc.neverExpires || !doc.expirationDate) {
    return approved;
  }
  return `${approved} · ${doc.expired ? 'Expired' : 'Expires'} ${formatDate(doc.expirationDate)}`;
}

/** What to upload, and where it comes from when Finance has named the office. */
function hint(required: RescueRequiredDocument, government: boolean): string {
  if (required.documentType === 'NONPROFIT_LETTER') {
    return government
      ? 'Proof that your shelter is run by a government body.'
      : 'Your IRS determination letter, or a Tax Exempt Organization Search printout.';
  }
  if (required.bucket === 'MULTISTATE_F0003') {
    return 'Your completed Form F0003.';
  }
  return required.issuingAgency
    ? `The exemption certificate issued by the ${required.issuingAgency}.`
    : 'Your state exemption certificate.';
}

/**
 * One thing the rescue is asked for, whether or not they have filed it.
 *
 * An empty row is the point rather than an omission: the certificate for the state they ship to is
 * offered before they ever open an order, so the document can be in review by the time they need it.
 */
export default function DocumentRow({
  required,
  uploading,
  onPickFile,
  government,
}: {
  required: RescueRequiredDocument;
  uploading: boolean;
  onPickFile: () => void;
  government: boolean;
}) {
  const doc = required.document;
  const isLetter = required.documentType === 'NONPROFIT_LETTER';
  // Replace goes away once Finance approves, and comes back when the approval is lapsing.
  const replaceable = doc !== null && (doc.status !== 'APPROVED' || doc.expired || expiringSoon(doc));

  const body = doc ? (
    <FileCard
      name={doc.originalFileName}
      sub={
        isLetter
          ? `${formatFileSize(doc.sizeBytes)} · uploaded ${formatDate(doc.uploadedAt)}`
          : certificateSubtitle(doc)
      }
    >
      {!isLetter && <StatusPill tone={tone(doc)}>{certificatePill(doc)}</StatusPill>}
      <FileLink href={doc.downloadUrl}>View</FileLink>
      {replaceable && (
        <FileButton onClick={onPickFile} disabled={uploading}>
          {uploading ? 'Uploading…' : 'Replace'}
        </FileButton>
      )}
    </FileCard>
  ) : (
    <UploadBox
      compact
      title={`Upload your ${isLetter ? 'letter' : 'certificate'}`}
      hint={`${hint(required, government)} PDF up to 20MB.`}
      onUpload={onPickFile}
      uploading={uploading}
    />
  );

  const reason = doc?.status === 'REJECTED' && doc.rejectionReason && (
    <div className={styles.rejectionNote}>
      <b>Reason:</b> {doc.rejectionReason}
    </div>
  );

  if (isLetter) {
    return (
      <div>
        {body}
        {doc && (
          <StatusPill tone={tone(doc)} className={styles.letterStatus}>
            {letterStatus(doc)}
          </StatusPill>
        )}
        {reason}
      </div>
    );
  }

  return (
    <div className={styles.stateRow}>
      <div className={styles.stateRowMain}>
        <div className={styles.stateName}>
          <b>{required.state ? stateName(required.state) : 'State'}</b>
          <span>{certificateFormName(required)}</span>
        </div>
        {body}
      </div>
      {reason}
    </div>
  );
}
